import React, { useState } from 'react';
import { Clock, CheckSquare, Pause, Square, Loader2, Play } from 'lucide-react';
import { JiraBadge, useProjectJiraUrl } from '../JiraBadge';
import { RichTextContent } from '../ui/RichTextEditor';
import {
  Task,
  TaskPriorityLabels,
  TaskPriorityColors
} from '../../types/task';

interface TaskPresentationCardProps {
  task: Task;
  onStatusChange: (taskId: string, newStatus: Task['status']) => void;
  isUpdating?: boolean;
  exportMode?: boolean;
}

export const TaskPresentationCard: React.FC<TaskPresentationCardProps> = ({ 
  task, 
  onStatusChange,
  isUpdating = false,
  exportMode = false
}) => {
  const [showActions, setShowActions] = useState(false);
  const jiraUrl = useProjectJiraUrl(task.projectId);

  const handleStatusChange = (newStatus: Task['status']) => {
    onStatusChange(task._id, newStatus);
    setShowActions(false);
  };

  const getStatusIcon = () => {
    const handleIconClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (task.status === 'encerrada') {
        handleStatusChange('pendente');
      } else {
        handleStatusChange('encerrada');
      }
    };

    switch (task.status) {
      case 'encerrada':
        return (
          <button 
            onClick={handleIconClick}
            className="hover:scale-110 transition-transform duration-200"
            title="Clique para reativar"
          >
            <CheckSquare className="w-4 h-4 corporate-text-success" />
          </button>
        );
      case 'pausado':
        return (
          <button 
            onClick={handleIconClick}
            className="hover:scale-110 transition-transform duration-200"
            title="Clique para encerrar"
          >
            <Pause className="w-4 h-4 corporate-text-warning" />
          </button>
        );
      default:
        return (
          <button 
            onClick={handleIconClick}
            className="hover:scale-110 transition-transform duration-200"
            title="Clique para encerrar"
          >
            <Square className="w-4 h-4 corporate-text-accent" />
          </button>
        );
    }
  };

  const getAvailableActions = () => {
    const actions = [];
    
    if (task.status !== 'pausado') {
      actions.push({
        status: 'pausado' as const,
        label: 'Pausar',
        icon: Pause,
        color: 'corporate-text-warning'
      });
    }
    
    if (task.status !== 'encerrada') {
      actions.push({
        status: 'encerrada' as const,
        label: 'Encerrar',
        icon: CheckSquare,
        color: 'corporate-text-success'
      });
    }

    if (task.status !== 'pendente') {
      actions.push({
        status: 'pendente' as const,
        label: 'Reativar',
        icon: Square,
        color: 'corporate-text-accent'
      });
    }

    return actions;
  };

  const getStatusClass = () => {
    switch (task.status) {
      case 'encerrada':
        return 'completed';
      case 'pausado':
        return 'paused';
      default:
        return 'in-progress';
    }
  };

  return (
    <div 
      className={`corporate-task-card ${getStatusClass()} ${
        task.status === 'encerrada' ? 'opacity-75' : ''
      } ${showActions ? 'z-10' : ''} relative`}
    >
      {/* Actions - Positioned at top right - Hidden in export mode */}
      <div className={`absolute top-0 right-0 print:hidden ${exportMode ? 'hidden' : ''}`}>
        {isUpdating ? (
          <div className="p-1">
            <Loader2 className="w-4 h-4 animate-spin corporate-text-accent" />
          </div>
        ) : (
          <>
            <button
              onClick={() => setShowActions(!showActions)}
              className="text-corporate-text-tertiary hover:text-corporate-text-primary transition-colors duration-200 p-1 leading-none"
            >
              ⋮
            </button>

            {showActions && (
              <div className="absolute right-0 top-8 z-50 w-40 bg-white border border-corporate-border-primary rounded-lg shadow-xl py-2">
                {getAvailableActions().map((action) => {
                  const IconComponent = action.icon;
                  return (
                    <button
                      key={action.status}
                      onClick={() => handleStatusChange(action.status)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-corporate-bg-hover rounded flex items-center gap-2 ${action.color}`}
                    >
                      <IconComponent className="w-3 h-3" />
                      {action.label}
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      <div className="corporate-task-header">
        {/* Left Section - Status Icon and Title */}
        <div className="flex items-start gap-3 flex-1 min-w-0 pr-8">
          <div className="pt-0.5 flex-shrink-0">
            {getStatusIcon()}
          </div>
          <div className="flex-1 min-w-0 overflow-hidden">
            <h4 className={`corporate-task-title font-medium ${
              task.status === 'encerrada' ? 'corporate-text-success' : ''
            }`}>
              {task.name}
            </h4>
            {/* Descrição e Tags */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mt-1">
              {task.description ? (
                <RichTextContent
                  content={task.description}
                  className="corporate-task-description break-words flex-1 mb-0 text-sm text-foreground-tertiary"
                />
              ) : (
                <div className="flex-1"></div>
              )}
              {/* Tags - design limpo para PDF */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`text-xs px-2 py-0.5 rounded ${TaskPriorityColors[task.prioridade]}`}>
                  {TaskPriorityLabels[task.prioridade]}
                </span>
                {task.status === 'encerrada' && (
                  <span className="text-xs text-gray-500 font-normal">
                    ✓ Concluída
                  </span>
                )}
                <JiraBadge 
                  task={task} 
                  jiraUrl={jiraUrl}
                  size="sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Section - Hours */}
        <div className="flex items-start flex-shrink-0 ml-auto">
          <div className="corporate-task-meta">
            <Clock className="w-4 h-4" />
            {task.horasEstimadas}h
          </div>
        </div>
      </div>

      {/* Overlay para fechar menu */}
      {showActions && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowActions(false)}
        />
      )}
    </div>
  );
};